import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { ModalService } from 'src/app/components/modals/modal.service';
import { SafeDeleteModalComponent } from 'src/app/components/modals/safe-delete-modal/safe-delete-modal.component';
import { CompanyService } from 'src/app/settings/services/company.service';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';

@Component({
  selector: 'company-details',
  templateUrl: './company-details.component.html',
  styleUrls: ['./company-details.component.scss'],
  standalone: false,
})
export class CompanyDetailsComponent
  extends BaseEditableComponent<any>
  implements OnInit, AfterViewInit
{
  @ViewChild('companyForm') companyForm: NgForm;

  @Input() companyId: string;

  actions = {
    saveAction: {
      id: 'saveButton',
      label: 'Save',
      action: () => this.save(),
      requireRole: 'ua sa',
      disabled: false,
    },
    cancelAction: {
      id: 'cancelButton',
      label: 'Cancel',
      action: () => this.cancel(),
    },
    deleteAction: {
      id: 'deleteButton',
      label: 'Delete',
      action: () => this.delete(),
      requireRole: 'ua sa',
    },
    moreOptions: [
      {
        id: 'exportButton',
        label: 'Export',
        action: () => this.export(),
        requireRole: 'sa',
      },
    ],
  };

  constructor(
    private _location: Location,
    public companyService: CompanyService,
    protected modalService: ModalService,
  ) {
    super();
  }

  ngOnInit() {
    if (this.companyId) {
      this.companyService.getCompany(this.companyId).then(() => {
        this.oldObject = this.cloneDeep(this.companyService.company);
      });
    }
  }

  ngAfterViewInit() {
    if (this.companyForm) {
      this.companyForm.statusChanges.subscribe(() => {
        this.actions.saveAction.disabled = this.companyForm.invalid;
      });
    }
  }

  canDeactivate(): boolean {
    return (
      !this.hasInitialObject() ||
      this.isDeepEqual(this.oldObject, this.companyService.company)
    );
  }

  async save() {
    if (!this.companyForm.valid) {
      console.log('Form not valid');
      return;
    }

    try {
      await this.companyService.save(this.companyForm, this.oldObject);

      this.oldObject = this.cloneDeep(this.companyService.company);
    } catch (error) {
      // Error is already handled and displayed by companyService
      // Don't update oldObject so canDeactivate() still detects unsaved changes
    }
  }

  delete() {
    return this.modalService
      .showMediumDialog(SafeDeleteModalComponent, {
        name: this.companyService.company.name,
      })
      .then(async () => {
        this.bypassUnsavedChanges();
        try {
          await this.companyService.deleteCompany(this.companyService.company);
        } catch (error) {
          this.resetUnsavedChanges();
        }
      });
  }

  export() {
    return this.companyService.exportCompany();
  }

  resetAuthKey() {
    var type = 'authKey';
    var title = 'Reset Authentication Key',
      message =
        'Resetting the Company Authentication Key will cause existing Data Gadgets ' +
        'to no longer report data until they are updated with the new Key.';
    var focusId = 'resetAuthKey';

    return this._resetCompanyField(type, title, message, focusId).then(
      (resp) => {
        if (resp && resp.item) {
          this.companyService.company.authKey = resp.item;
        }
      },
    );
  }

  resetClaimId() {
    var type = 'claimId';
    var title = 'Reset Claim Id',
      message =
        'Resetting the Company Claim Id will cause existing installations to no ' +
        'longer be associated with your Company.';
    var focusId = 'claimId';

    return this._resetCompanyField(type, title, message, focusId).then(
      (resp) => {
        if (resp && resp.item) {
          this.companyService.company.claimId = resp.item;
        }
      },
    );
  }

  _resetCompanyField(type, title, message, focusId) {
    return this.modalService
      .confirm(title, message, 'Ok', 'Cancel', focusId)
      .then(() => {
        return this.companyService.resetCompanyField(type);
      });
  }

  cancel() {
    this.bypassUnsavedChanges();
    this._location.back();
  }
}
